import type { Project, Tag } from '@/types';
import { cn } from '@/lib/utils';
import { Button } from '@astryxdesign/core/Button';
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Selector } from '@astryxdesign/core/Selector';
import { Text } from '@astryxdesign/core/Text';
import { TextArea } from '@astryxdesign/core/TextArea';
import { TimeInput } from '@astryxdesign/core/TimeInput';
import { Token } from '@astryxdesign/core/Token';
import { createISOTimeString, type ISOTimeString } from '@astryxdesign/core/utils';
import { Plus, X } from 'lucide-react';
import { type CSSProperties, useMemo, useState } from 'react';
import type { DraftActivity, DraftRow } from '../lib/draft';
import {
  formatTimeRange,
  minutesBetween,
  minutesFromTime,
  newActivity,
  newTimelineRow,
  nextTimelineEnd,
  nextTimelineStart,
  rowMinutes,
  timeRangeErrors as getTimeRangeErrors,
} from '../lib/draft';
import { formatDuration } from '../lib/format';
import { TagPicker, tagTokenColor } from './tag-picker';

const HOUR_HEIGHT = 64;
const MINUTE_HEIGHT = HOUR_HEIGHT / 60;
const TIMELINE_START_MINUTES = 7 * 60;
const TIMELINE_END_MINUTES = 30 * 60;
const TIMELINE_MINUTES = TIMELINE_END_MINUTES - TIMELINE_START_MINUTES;
const TIMELINE_HOURS = TIMELINE_MINUTES / 60;

interface TimelineReportProps {
  rows: DraftRow[];
  timeRangeErrors: ReadonlyMap<string, string>;
  tags: Tag[];
  projects: Project[];
  readOnly: boolean;
  projectColor: (row: DraftRow, index: number) => string;
  onChangeRow: (row: DraftRow) => void;
  onRemoveRow: (row: DraftRow) => void;
  onAddRow: (row: DraftRow) => void;
  onCreateTag: (
    rowId: string,
    activityId: string,
    label: string,
    onCreated?: (tagId: string) => void
  ) => void;
}

interface TimelineCompactBlockProps {
  row: DraftRow;
  color: string;
  tags: Tag[];
  readOnly: boolean;
  timeRangeError?: string;
  style?: CSSProperties;
  onEdit: () => void;
}

interface TimelineBlockEditorProps {
  row: DraftRow;
  color: string;
  tags: Tag[];
  projects: Project[];
  readOnly: boolean;
  projectError?: string;
  timeRangeError?: string;
  hasInlineRemove?: boolean;
  onChange: (row: DraftRow) => void;
  onRemove: () => void;
  onCreateTag: (activityId: string, label: string) => void;
}

interface TimelineActivityProps {
  activity: DraftActivity;
  tags: Tag[];
  readOnly: boolean;
  onChange: (patch: Partial<DraftActivity>) => void;
  onRemove: () => void;
  onCreateTag: (label: string) => void;
}

export default function TimelineReport({
  rows,
  timeRangeErrors,
  tags,
  projects,
  readOnly,
  projectColor,
  onChangeRow,
  onRemoveRow,
  onAddRow,
  onCreateTag,
}: TimelineReportProps) {
  const [editingRow, setEditingRow] = useState<DraftRow | null>(null);
  const [touchedProject, setTouchedProject] = useState(false);
  const scheduledRows = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.startTime && row.endTime)
    .sort(
      (left, right) =>
        timelineOffsetMinutes(left.row.startTime) - timelineOffsetMinutes(right.row.startTime)
    );
  const unscheduledRows = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !row.startTime || !row.endTime);
  const editingRowIndex = editingRow ? rows.findIndex((row) => row.id === editingRow.id) : -1;
  const editingRowColorIndex = editingRowIndex >= 0 ? editingRowIndex : rows.length;
  const editingRows = editingRow
    ? [...rows.filter((row) => row.id !== editingRow.id), editingRow]
    : rows;
  const editingTimeRangeError = editingRow
    ? getTimeRangeErrors(editingRows).get(editingRow.id)
    : undefined;
  const hasMissingProject = editingRow && !editingRow.projectId;
  const editingProjectError = hasMissingProject && touchedProject ? 'elegí un proyecto' : undefined;
  const hasEditingError = Boolean(hasMissingProject || editingTimeRangeError);

  const closeEditor = () => {
    setEditingRow(null);
    setTouchedProject(false);
  };

  const finishEditor = () => {
    if (!editingRow || readOnly) {
      closeEditor();
      return;
    }

    if (hasEditingError) {
      setTouchedProject(true);
      return;
    }

    if (editingRowIndex >= 0) {
      onChangeRow(editingRow);
    } else {
      onAddRow(editingRow);
    }

    closeEditor();
  };

  const addRow = () => {
    const startTime = nextTimelineStart(rows);
    const row = newTimelineRow(startTime, nextTimelineEnd(startTime));

    setTouchedProject(false);
    setEditingRow(row);
  };

  const removeEditingRow = (row: DraftRow) => {
    if (editingRowIndex >= 0) onRemoveRow(row);
    closeEditor();
  };

  const addTagToEditingActivity = (activityId: string, tagId: string) => {
    setEditingRow((current) =>
      current
        ? {
            ...current,
            activities: current.activities.map((activity) =>
              activity.id === activityId && !activity.tags.includes(tagId)
                ? { ...activity, tags: [...activity.tags, tagId] }
                : activity
            ),
          }
        : null
    );
  };

  return (
    <div className="iorio-reports-timeline">
      {unscheduledRows.length > 0 && (
        <section className="iorio-reports-unscheduled" aria-label="Entradas sin horario">
          <div className="iorio-reports-unscheduled-header">
            <Text type="supporting" weight="bold">
              Sin horario
            </Text>
            <Text type="supporting" color="secondary">
              Agregá desde y hasta para ubicar estas entradas en el día.
            </Text>
          </div>
          <div className="iorio-reports-unscheduled-list">
            {unscheduledRows.map(({ row, index }) => (
              <TimelineBlockEditor
                key={row.id}
                row={row}
                color={projectColor(row, index)}
                tags={tags}
                projects={projects}
                readOnly={readOnly}
                timeRangeError={timeRangeErrors.get(row.id)}
                onChange={onChangeRow}
                onRemove={() => onRemoveRow(row)}
                onCreateTag={(activityId, label) => onCreateTag(row.id, activityId, label)}
              />
            ))}
          </div>
        </section>
      )}

      {!readOnly && projects.length > 0 && (
        <button type="button" onClick={addRow} className="iorio-reports-add-project">
          <Plus size={16} strokeWidth={2} />
          Nueva entrada
        </button>
      )}

      <div
        className="iorio-reports-timeline-day"
        style={
          {
            '--timeline-hour-height': `${HOUR_HEIGHT}px`,
            '--timeline-height': `${TIMELINE_HOURS * HOUR_HEIGHT}px`,
          } as CSSProperties
        }
      >
        {Array.from({ length: TIMELINE_HOURS + 1 }, (_, index) => (
          <div
            key={index}
            className="iorio-reports-timeline-hour"
            style={{ top: `${index * HOUR_HEIGHT}px` }}
          >
            <span>{hourLabel(index)}</span>
          </div>
        ))}

        {scheduledRows.map(({ row, index }) => {
          const top = timelineOffsetMinutes(row.startTime) * MINUTE_HEIGHT;
          const height = Math.max(rowMinutes(row) * MINUTE_HEIGHT, 1);

          return (
            <TimelineCompactBlock
              key={row.id}
              row={row}
              color={projectColor(row, index)}
              tags={tags}
              readOnly={readOnly}
              timeRangeError={timeRangeErrors.get(row.id)}
              style={
                {
                  top,
                  height,
                  '--project-color': projectColor(row, index),
                } as CSSProperties
              }
              onEdit={() => {
                setTouchedProject(false);
                setEditingRow(row);
              }}
            />
          );
        })}
      </div>

      <Dialog
        isOpen={editingRow != null}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeEditor();
        }}
        purpose="form"
        width="min(720px, calc(100vw - 32px))"
        maxHeight="calc(100dvh - 32px)"
        className="iorio-reports-timeline-dialog"
      >
        {editingRow && (
          <div
            className="iorio-reports-timeline-dialog-shell"
            style={
              {
                '--project-color': projectColor(editingRow, editingRowColorIndex),
              } as CSSProperties
            }
          >
            <DialogHeader
              title={
                readOnly
                  ? 'Detalle de la entrada'
                  : editingRowIndex >= 0
                    ? 'Editar entrada'
                    : 'Nueva entrada'
              }
              subtitle={editorSubtitle(editingRow)}
              onOpenChange={(isOpen) => {
                if (!isOpen) closeEditor();
              }}
            />
            <div className="iorio-reports-timeline-dialog-body">
              <TimelineBlockEditor
                row={editingRow}
                color={projectColor(editingRow, editingRowColorIndex)}
                tags={tags}
                projects={projects}
                readOnly={readOnly}
                projectError={editingProjectError}
                timeRangeError={editingTimeRangeError}
                hasInlineRemove={false}
                onChange={setEditingRow}
                onRemove={() => removeEditingRow(editingRow)}
                onCreateTag={(activityId, label) =>
                  onCreateTag(editingRow.id, activityId, label, (tagId) =>
                    addTagToEditingActivity(activityId, tagId)
                  )
                }
              />
            </div>
            <div className="iorio-reports-timeline-dialog-footer">
              {!readOnly && (
                <Button
                  size="sm"
                  variant="destructive"
                  label={editingRowIndex >= 0 ? 'Quitar entrada' : 'Descartar entrada'}
                  onClick={() => removeEditingRow(editingRow)}
                />
              )}
              <Button
                size="sm"
                variant="primary"
                label={readOnly ? 'Cerrar' : 'Listo'}
                onClick={finishEditor}
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function TimelineCompactBlock({
  row,
  color,
  tags,
  readOnly,
  timeRangeError,
  style,
  onEdit,
}: TimelineCompactBlockProps) {
  const blockStyle = {
    ...style,
    '--project-color': color,
  } as CSSProperties;
  const firstActivity = row.activities.find((activity) => activity.text.trim());
  const activityCount = row.activities.filter((activity) => activity.text.trim()).length;
  const summary = firstActivity?.text.trim() || 'Sin actividades cargadas';
  const isHalfHour = rowMinutes(row) === 30;

  const blockTags = useMemo(() => {
    const tagIds = Array.from(new Set(row.activities.flatMap((activity) => activity.tags)));
    return tagIds
      .map((tagId) => tags.find((tag) => tag.id === tagId))
      .filter((tag): tag is Tag => tag != null && !tag.deleted);
  }, [row.activities, tags]);

  const visibleTags = blockTags.slice(0, 2);
  const overflowCount = Math.max(blockTags.length - visibleTags.length, 0);

  return (
    <button
      type="button"
      className={cn(
        'iorio-reports-timeline-block is-scheduled is-compact',
        isHalfHour && 'is-half-hour',
        timeRangeError && 'ring-error z-10 ring-2 ring-inset'
      )}
      style={blockStyle}
      onClick={onEdit}
      aria-invalid={timeRangeError ? true : undefined}
      aria-label={`${readOnly ? 'Ver' : 'Editar'} ${row.projectName || 'entrada sin proyecto'} ${formatTimeRange(row)}${
        timeRangeError ? `. Error: ${timeRangeError}` : ''
      }`}
      title={timeRangeError}
    >
      <span
        className="iorio-reports-timeline-accent"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span className="iorio-reports-timeline-compact-main">
        {isHalfHour ? (
          <span className="iorio-reports-timeline-compact-half-hour-title">{summary}</span>
        ) : (
          <>
            <span className="iorio-reports-timeline-compact-title">
              {row.projectName || 'Sin proyecto'}
            </span>
            <span className="iorio-reports-timeline-compact-summary">{summary}</span>
          </>
        )}
      </span>
      {!isHalfHour && (
        <span className="iorio-reports-timeline-compact-meta">
          <span className="iorio-reports-timeline-compact-time">{formatTimeRange(row)}</span>
        </span>
      )}
      {!isHalfHour && activityCount > 1 && (
        <span className="iorio-reports-timeline-compact-count">{activityCount} actividades</span>
      )}
      {!isHalfHour && visibleTags.length > 0 && (
        <div className="iorio-reports-timeline-compact-tags">
          {visibleTags.map((tag) => (
            <Token key={tag.id} label={tag.label} color={tagTokenColor(tag)} size="sm" />
          ))}
          {overflowCount > 0 && <Token label={`+${overflowCount}`} color="gray" size="sm" />}
        </div>
      )}
    </button>
  );
}

function TimelineBlockEditor({
  row,
  color,
  tags,
  projects,
  readOnly,
  projectError,
  timeRangeError,
  hasInlineRemove = true,
  onChange,
  onRemove,
  onCreateTag,
}: TimelineBlockEditorProps) {
  const blockStyle = {
    '--project-color': color,
  } as CSSProperties;
  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: project.name,
  }));
  const timeStatus = !readOnly && timeRangeError ? { type: 'error' as const } : undefined;

  const updateTime = (patch: Pick<Partial<DraftRow>, 'startTime' | 'endTime'>) => {
    const startTime = patch.startTime === undefined ? row.startTime : patch.startTime;
    const endTime = patch.endTime === undefined ? row.endTime : patch.endTime;

    onChange({
      ...row,
      ...patch,
      minutes: minutesBetween(startTime, endTime),
    });
  };

  const selectProject = (projectId: string) => {
    const project = projects.find((item) => item.id === projectId);
    if (project) {
      onChange({ ...row, projectId: project.id, projectName: project.name });
    }
  };

  const updateActivity = (activityId: string, patch: Partial<DraftActivity>) => {
    onChange({
      ...row,
      activities: row.activities.map((activity) =>
        activity.id === activityId ? { ...activity, ...patch } : activity
      ),
    });
  };

  return (
    <article className={cn('iorio-reports-timeline-block', 'is-editor')} style={blockStyle}>
      <span
        className="iorio-reports-timeline-accent"
        style={{ backgroundColor: color }}
        aria-hidden
      />

      <div className="iorio-reports-timeline-block-header">
        <div className="iorio-reports-timeline-project">
          {readOnly ? (
            <Text type="body" weight="bold">
              {row.projectName || 'Sin proyecto'}
            </Text>
          ) : (
            <Selector
              label="Proyecto"
              options={projectOptions}
              value={row.projectId ?? undefined}
              onChange={selectProject}
              placeholder="Elegí un proyecto"
              size="sm"
              hasSearch={projects.length > 8}
              status={projectError ? { type: 'error', message: projectError } : undefined}
            />
          )}
        </div>

        <div className="iorio-reports-timeline-timebar">
          <TimeInput
            label="Desde"
            value={timeInputValue(row.startTime)}
            onChange={(value) => updateTime({ startTime: value ?? null })}
            hourFormat="24h"
            increment={1}
            placeholder="00:00"
            size="sm"
            isDisabled={readOnly}
            status={timeStatus}
          />
          <TimeInput
            label="Hasta"
            value={timeInputValue(row.endTime)}
            onChange={(value) => updateTime({ endTime: value ?? null })}
            hourFormat="24h"
            increment={1}
            placeholder="00:00"
            size="sm"
            isDisabled={readOnly}
            status={
              timeStatus && timeRangeError ? { ...timeStatus, message: timeRangeError } : undefined
            }
          />
          <span className="iorio-reports-timeline-duration">{formatDuration(rowMinutes(row))}</span>
          {!readOnly && hasInlineRemove && (
            <IconButton
              label="Quitar entrada"
              tooltip="Quitar entrada"
              icon={<X size={15} />}
              variant="ghost"
              size="sm"
              onClick={onRemove}
            />
          )}
        </div>
      </div>

      <div className="iorio-reports-timeline-activities">
        {row.activities.map((activity) => (
          <TimelineActivity
            key={activity.id}
            activity={activity}
            tags={tags}
            readOnly={readOnly}
            onChange={(patch) => updateActivity(activity.id, patch)}
            onRemove={() =>
              onChange({
                ...row,
                activities: row.activities.filter((item) => item.id !== activity.id),
              })
            }
            onCreateTag={(label) => onCreateTag(activity.id, label)}
          />
        ))}
      </div>

      {!readOnly && (
        <button
          type="button"
          className="iorio-reports-timeline-add-activity"
          onClick={() => onChange({ ...row, activities: [...row.activities, newActivity()] })}
        >
          <Plus size={14} strokeWidth={2.6} />
          Actividad
        </button>
      )}
    </article>
  );
}

function TimelineActivity({
  activity,
  tags,
  readOnly,
  onChange,
  onRemove,
  onCreateTag,
}: TimelineActivityProps) {
  const appliedTags = activity.tags
    .map((tagId) => tags.find((tag) => tag.id === tagId))
    .filter((tag) => tag != null);

  return (
    <div className="iorio-reports-timeline-activity">
      <div className="iorio-reports-timeline-activity-main">
        <TextArea
          label="Actividad"
          isLabelHidden
          value={activity.text}
          onChange={(value) => onChange({ text: value })}
          placeholder="Describe una actividad…"
          rows={2}
          size="sm"
          isDisabled={readOnly}
        />
        {!readOnly && (
          <IconButton
            label="Quitar actividad"
            tooltip="Quitar actividad"
            icon={<X size={14} />}
            variant="ghost"
            size="sm"
            onClick={onRemove}
          />
        )}
      </div>

      {(appliedTags.length > 0 || !readOnly) && (
        <div className="iorio-reports-timeline-tags">
          {appliedTags.map((tag) => (
            <Token
              key={tag.id}
              label={tag.label}
              color={tagTokenColor(tag)}
              size="sm"
              onRemove={
                readOnly
                  ? undefined
                  : () => onChange({ tags: activity.tags.filter((tagId) => tagId !== tag.id) })
              }
            />
          ))}
          {!readOnly && (
            <TagPicker
              tags={tags}
              appliedTagIds={activity.tags}
              onAdd={(tagId) => onChange({ tags: [...activity.tags, tagId] })}
              onCreate={onCreateTag}
            />
          )}
        </div>
      )}
    </div>
  );
}

function hourLabel(index: number): string {
  const hour = (TIMELINE_START_MINUTES / 60 + index) % 24;

  return `${hour.toString().padStart(2, '0')}:00`;
}

function editorSubtitle(row: DraftRow): string {
  if (row.startTime && row.endTime) {
    return formatTimeRange(row);
  } else {
    return 'Completá desde y hasta para ubicarlo en el timeline';
  }
}

function timelineOffsetMinutes(value: string | null): number {
  const minutes = minutesFromTime(value);
  if (minutes == null) return 0;

  if (minutes >= TIMELINE_START_MINUTES) {
    return minutes - TIMELINE_START_MINUTES;
  } else {
    return minutes + 24 * 60 - TIMELINE_START_MINUTES;
  }
}

function timeInputValue(value: string | null): ISOTimeString | undefined {
  if (!value) return undefined;

  return createISOTimeString(value) ?? undefined;
}
